/* eslint-disable */
import axios from 'axios';
import { HTTP_SERVICE_REQUEST_OBSERVE_BODY, HTTP_SERVICE_REQUEST_OBSERVE_RESPONSE } from '../../Constants/Http/Http.constants';
import throwHttpCancelled from '../../Errors/Http/HttpCancelled.error';
import throwHttpInvalidObserveError from '../../Errors/Http/HttpInvalidObserve.error';
import throwHttpNotOkError from '../../Errors/Http/HttpNotOk.error';
import './Http.service.i18n';

const { CancelToken } = axios;

/**
 * HttpService
 * @class HttpService
 */
class HttpService {
  /**
   * The cancel token for the currently executing request
   * @type {number}
   * @memberof HttpService
   */
  _cancelToken = null;

  /**
   * Handle the responses from the http service
   * @param {Response} response The http Response Object.
   * @param {HttpServiceRequestObserve} [observe=HTTP_SERVICE_REQUEST_OBSERVE_BODY] Returns body or (whole) response.
   * @returns {Promise<any>} The data from the handle response
   * @memberof HttpService
   */
  _handleResponse = (response, observe = HTTP_SERVICE_REQUEST_OBSERVE_BODY) => {
    if (observe === HTTP_SERVICE_REQUEST_OBSERVE_BODY) {
      return response.data;
    }
    if (observe === HTTP_SERVICE_REQUEST_OBSERVE_RESPONSE) {
      return response;
    }
    return throwHttpInvalidObserveError(observe);
  };

  /**
   * Handle the error response from the http service
   * @param {any} error The http error Object.
   * @returns {never} The data from the handle response
   * @memberof HttpService
   */
  _handleError = (error) => {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    if (error.response) {
      return throwHttpNotOkError(error.response.status, error.response);
    }
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    if (error.request) {
      return throwHttpNotOkError(error.request);
    }
    if (axios.isCancel(error)) {
      return throwHttpCancelled(error.message);
    }
    // Something happened in setting up the request that triggered an Error
    return throwHttpNotOkError(error.message);
  };

  /**
   * Prepare the headers for consumption by the http service
   * @param {Headers} [headers] The headers to prepare
   * @returns {any} The formated headers
   * @memberof HttpService
   */
  _prepareHeaders = (headers = new Headers()) => {
    if (headers instanceof Headers) {
      const newHeaders = {};
      if (!headers.has(`Content-Type`)) {
        headers.append(`Content-Type`, `application/json;charset=utf-8`);
      }
      headers.forEach((value, key) => {
        newHeaders[key] = value;
      });
      return newHeaders;
    }
    return {
      'Content-Type': `application/json;charset=utf-8`,
      ...headers,
    };
  };

  /**
   * Prepare the headers for consumption by the http service
   * @param {HttpServiceRequestDto} data request required params to make the request
   * @returns {any} The formated request
   * @memberof HttpService
   */
  _prepareRequest = ({ headers, method, params, withCredentials }) => {
    return {
      headers: this._prepareHeaders(headers),
      method,
      params,
      withCredentials,
    };
  };

  /**
   * Sets the cancellation token
   * @param {string} message The cancellation message.
   * @returns {void} void
   * @memberof HttpService
   */
  setCancelToken = (message) => {
    this.cancel(message);
    this._cancelToken = CancelToken.source();
  };

  /**
   * Cancels the inflight request
   * @param {string} message The cancellation message.
   * @returns {void} void
   * @memberof HttpService
   */
  cancel = (message) => {
    if (this._cancelToken) {
      this._cancelToken.cancel(message);
      this._cancelToken = null;
    }
  };

  /**
   * Perform a **GET** request to the given `url`
   * @param {string} url The `url` to request
   * @param {HttpServiceRequestDto} options The required params to make a **GET** request
   * @returns {Promise<any>} The requested resource
   * @memberof HttpService
   */
  get = async (url, { data, headers, observe, params, withCredentials } = {}) => {
    const cancellation = this._cancelToken && {
      cancelToken: this._cancelToken.token,
    };
    const requestParams = this._prepareRequest({
      headers,
      method: `GET`,
      params,
      withCredentials,
    });
    try {
      const response = await axios.get(url, {
        ...cancellation,
        ...requestParams,
        data,
      });
      return this._handleResponse(response, observe);
    } catch (error) {
      return this._handleError(error);
    }
  };

  /**
   * Perform a **POST** request to the given `url`
   * @param {string} url The `url` to request
   * @param {HttpServiceRequestDto} options The required params to make a **POST** request
   * @returns {Promise<any>} The requested resource
   * @memberof HttpService
   */
  post = async (url, { data, headers, observe, params, withCredentials } = {}) => {
    const cancellation = this._cancelToken && {
      cancelToken: this._cancelToken.token,
    };
    const requestParams = this._prepareRequest({
      ...cancellation,
      headers,
      method: `POST`,
      params,
      withCredentials,
    });
    try {
      const response = await axios.post(url, data, requestParams);
      return this._handleResponse(response, observe);
    } catch (error) {
      return this._handleError(error);
    }
  };

  /**
   * Perform a **PUT** request to the given `url`
   * @param {string} url The `url` to request
   * @param {HttpServiceRequestDto} options The required params to make a **PUT** request
   * @returns {Promise<any>} The requested resource
   * @memberof HttpService
   */
  put = async (url, { data, headers, observe, params, withCredentials } = {}) => {
    const cancellation = this._cancelToken && {
      cancelToken: this._cancelToken.token,
    };
    const requestParams = this._prepareRequest({
      ...cancellation,
      headers,
      method: `PUT`,
      params,
      withCredentials,
    });
    try {
      const response = await axios.put(url, data, requestParams);
      return this._handleResponse(response, observe);
    } catch (error) {
      return this._handleError(error);
    }
  };
}

export default HttpService;
